import { AddressChangeReview } from "./address-change-review";
import {
  requiresSameWorkReview,
  ShipmentWorkReview,
} from "./shipment-work-review";
import { useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Check,
  X,
  Loader2,
  Clock,
  MapPin,
  Phone,
  Mail,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";
import {
  usePendingShipmentChangeRequests,
  useHandleShipmentChangeRequest,
  type PendingShipmentChangeRequest,
  formatShipmentChangeRequestType,
} from "@/api/shipment-change-requests";
import { Link } from "react-router";
import { useCarrierScope } from "@/contexts/carrier-scope-context";

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getTimeSince(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  return `${diffMins}m ago`;
}

function getRequestTypeBadgeColor(type: string): string {
  if (type.includes("ADDRESS")) return "bg-orange-100 text-orange-800";
  if (type.includes("CONTACT")) return "bg-blue-100 text-blue-800";
  if (type.includes("INSTRUCTIONS")) return "bg-green-100 text-green-800";
  if (type.includes("RESCHEDULE")) return "bg-purple-100 text-purple-800";
  return "bg-gray-100 text-gray-800";
}

function describeRequestedInstructions(
  request: PendingShipmentChangeRequest
): string | null {
  if (request.deliveryInstructions == null) return null;
  if (request.deliveryInstructions !== "") return request.deliveryInstructions;
  return request.requestTypes.includes("RECIPIENT_DELIVERY_INSTRUCTIONS")
    ? "Clear delivery instructions"
    : "Clear pickup instructions";
}

interface HandleDialogProps {
  request: PendingShipmentChangeRequest | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action: "action" | "reject";
  onDecline: () => void;
}

function canMarkActioned(request: PendingShipmentChangeRequest): boolean {
  return !request.requestTypes.some(
    (type) => type === "DELIVERY_RESCHEDULE" || type === "PICKUP_RESCHEDULE"
  );
}

function HandleRequestDialog({
  request,
  open,
  onOpenChange,
  action,
  onDecline,
}: HandleDialogProps) {
  const reviewTitleRef = useRef<HTMLHeadingElement>(null);
  const [adminResponse, setAdminResponse] = useState("");
  const [sameWorkConfirmed, setSameWorkConfirmed] = useState(false);
  const needsWorkReview = Boolean(request && requiresSameWorkReview(request));
  const handleRequest = useHandleShipmentChangeRequest();

  const handleSubmit = async () => {
    if (!request) return;
    if (action === "action" && needsWorkReview && !sameWorkConfirmed) return;

    if (action === "reject" && !adminResponse.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    try {
      await handleRequest.mutateAsync({
        id: request.id,
        status: action === "action" ? "ACTIONED" : "CANNOT_FULFILL",
        adminResponse: adminResponse.trim() || undefined,
        ...(action === "action" && needsWorkReview
          ? { sameAcceptedWorkConfirmed: sameWorkConfirmed }
          : {}),
      });
      toast.success(
        action === "action" ? "Change applied" : "Request rejected",
        {
          description: `SHP-${request.shipmentNumber} change request has been processed.`,
        }
      );
      onOpenChange(false);
      setAdminResponse("");
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { error?: string; message?: string } };
        message?: string;
      };
      toast.error("Failed to process request", {
        description:
          err.response?.data?.message ??
          err.response?.data?.error ??
          err.message,
      });
    }
  };

  if (
    request &&
    action === "action" &&
    (request.addressChange ||
      request.requestTypes.some(
        (type) =>
          type.endsWith("ADDRESS_CORRECTION") ||
          type === "PACKAGE_DECLARATION_AMENDMENT"
      ))
  ) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="flex max-h-[90dvh] min-w-0 flex-col overflow-hidden p-0 sm:max-w-4xl"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            reviewTitleRef.current?.focus();
          }}
        >
          <DialogHeader className="shrink-0 border-b px-6 py-5 pr-12 text-left">
            <DialogTitle
              ref={reviewTitleRef}
              tabIndex={-1}
              className="outline-none"
            >
              SHP-{request.shipmentNumber} · Change request
            </DialogTitle>
            <DialogDescription className="sr-only">
              Review the requested change.
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 min-w-0 overflow-y-auto px-6 py-5">
            <AddressChangeReview
              key={request.id}
              request={request}
              endpoint={`${import.meta.env.VITE_API_URL}/api/v2/change-requests/${request.id}`}
              isAdmin
              onFinished={(state) => {
                toast.success(
                  state === "APPLIED"
                    ? "Address correction applied"
                    : state === "RECONCILED_FOR_CANCELLATION"
                      ? "Correction reconciled; cancellation review remains pending"
                      : "Fresh correction review created"
                );
                onOpenChange(false);
              }}
            />
          </div>
          <DialogFooter className="shrink-0 flex-row items-center justify-end border-t px-6 py-3">
            <Button variant="ghost" onClick={onDecline}>
              Decline request
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {action === "action" ? "Apply change" : "Cannot Fulfill Request"}
          </DialogTitle>
          <DialogDescription>
            SHP-{request?.shipmentNumber} - {request?.trackingNumber}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Request Details */}
          <div className="p-3 bg-muted rounded-lg space-y-2">
            <p className="text-sm font-medium">Requested Changes:</p>
            <div className="flex flex-wrap gap-1">
              {request?.requestTypes.map((type) => (
                <Badge
                  key={type}
                  variant="outline"
                  className={getRequestTypeBadgeColor(type)}
                >
                  {formatShipmentChangeRequestType(type)}
                </Badge>
              ))}
            </div>

            {request && describeRequestedInstructions(request) && (
              <div className="text-sm">
                <span className="font-medium">Instructions:</span>{" "}
                {describeRequestedInstructions(request)}
              </div>
            )}
            {request?.addressCorrection && (
              <div className="text-sm">
                <span className="font-medium">Address:</span>{" "}
                {request.addressCorrection}
              </div>
            )}
            {request?.addressLine2Correction != null && (
              <div className="text-sm">
                <span className="font-medium">Requested unit:</span>{" "}
                {request.addressLine2Correction || "Remove unit"}
              </div>
            )}
            {(request?.addressLine2Correction != null ||
              request?.requestTypes.includes(
                "RECIPIENT_DELIVERY_INSTRUCTIONS"
              )) && (
              <p className="text-sm text-amber-800">
                Existing labels are not updated automatically. Check the label
                before the next handoff.
              </p>
            )}
            {request && !canMarkActioned(request) && (
              <p className="text-sm text-amber-800">
                This request needs manual support review before it can be
                applied. The current address has not changed.
              </p>
            )}
            {request?.contactName && (
              <div className="text-sm">
                <span className="font-medium">Contact:</span>{" "}
                {request.contactName}
                {request.contactPhone && ` - ${request.contactPhone}`}
                {request.contactEmail && ` - ${request.contactEmail}`}
              </div>
            )}
            {request?.rescheduleDate && (
              <div className="text-sm">
                <span className="font-medium">Reschedule:</span>{" "}
                {new Date(request.rescheduleDate).toLocaleDateString()}
                {request.reschedulePreference &&
                  ` (${request.reschedulePreference})`}
              </div>
            )}
            {request?.additionalNotes && (
              <div className="text-sm">
                <span className="font-medium">Notes:</span>{" "}
                {request.additionalNotes}
              </div>
            )}
          </div>

          {/* Admin Response */}
          {request && action === "action" && (
            <ShipmentWorkReview
              request={request}
              checked={sameWorkConfirmed}
              onCheckedChange={setSameWorkConfirmed}
              disabled={handleRequest.isPending}
            />
          )}
          <div className="space-y-2">
            <Label htmlFor="response">
              {action === "action"
                ? "Response (optional)"
                : "Reason for rejection"}
            </Label>
            <Textarea
              id="response"
              placeholder={
                action === "action"
                  ? "Add any notes about the changes made..."
                  : "Explain why this request cannot be fulfilled..."
              }
              value={adminResponse}
              onChange={(e) => setAdminResponse(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              handleRequest.isPending ||
              (action === "action" && needsWorkReview && !sameWorkConfirmed)
            }
            variant={action === "action" ? "default" : "destructive"}
          >
            {handleRequest.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Processing...
              </>
            ) : action === "action" ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                Apply change
              </>
            ) : (
              <>
                <X className="h-4 w-4 mr-2" />
                Cannot Fulfill
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PendingShipmentChangeRequests() {
  // Change-requests endpoint only accepts single `carrierId` /
  // `dispatchCenterId` — not the multi-id shape from ezx-server PR #277.
  // Pick the first selected for now; multi-narrowing is a follow-up if the
  // server endpoint grows the new shape.
  const { carrierIds, dispatchCenterIds } = useCarrierScope();
  const [selectedRequest, setSelectedRequest] =
    useState<PendingShipmentChangeRequest | null>(null);
  const [dialogAction, setDialogAction] = useState<"action" | "reject">(
    "action"
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data, isLoading, error } = usePendingShipmentChangeRequests(50, 0, {
    carrierId: carrierIds[0],
    dispatchCenterId: dispatchCenterIds[0],
  });

  const handleAction = (
    request: PendingShipmentChangeRequest,
    action: "action" | "reject"
  ) => {
    setSelectedRequest(request);
    setDialogAction(action);
    setDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Pending Change Requests</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Pending Change Requests</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-500">Failed to load change requests</p>
        </CardContent>
      </Card>
    );
  }

  const requests = data?.requests ?? [];

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Pending Shipment Change Requests
            {requests.length > 0 && (
              <Badge variant="secondary">{requests.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Check className="h-12 w-12 mx-auto mb-2 text-green-500" />
              <p>No pending change requests</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Shipment</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Request Types</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <div>
                        <Link
                          to={`/fulfillment/shipments/${request.shipmentId}`}
                          className="font-medium text-primary hover:underline"
                        >
                          SHP-{request.shipmentNumber}
                        </Link>
                        <p className="text-xs text-muted-foreground font-mono">
                          {request.trackingNumber}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {request.submittedBy.type === "CARRIER"
                            ? "Carrier"
                            : request.submittedBy.type === "ADMIN"
                              ? "EZX"
                              : "Customer"}
                          : {request.submittedBy.name}
                        </p>
                        {request.submittedBy.email && (
                          <p className="text-xs text-muted-foreground">
                            {request.submittedBy.email}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {request.requestTypes.map((type) => (
                          <Badge
                            key={type}
                            variant="outline"
                            className={`text-xs ${getRequestTypeBadgeColor(type)}`}
                          >
                            {formatShipmentChangeRequestType(type)}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-xs max-w-[200px]">
                        {describeRequestedInstructions(request) && (
                          <div className="flex items-start gap-1">
                            <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                            <span className="truncate">
                              {describeRequestedInstructions(request)}
                            </span>
                          </div>
                        )}
                        {request.contactPhone && (
                          <div className="flex items-center gap-1">
                            <Phone className="h-3 w-3 shrink-0" />
                            <span>{request.contactPhone}</span>
                          </div>
                        )}
                        {request.contactEmail && (
                          <div className="flex items-center gap-1">
                            <Mail className="h-3 w-3 shrink-0" />
                            <span className="truncate">
                              {request.contactEmail}
                            </span>
                          </div>
                        )}
                        {request.rescheduleDate && (
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 shrink-0" />
                            <span>
                              {new Date(
                                request.rescheduleDate
                              ).toLocaleDateString()}
                              {request.reschedulePreference &&
                                ` (${request.reschedulePreference})`}
                            </span>
                          </div>
                        )}
                        {request.addressCorrection && (
                          <div className="flex items-start gap-1">
                            <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                            <span className="truncate">
                              {request.addressCorrection}
                            </span>
                          </div>
                        )}
                        {request.addressLine2Correction != null && (
                          <div className="flex items-start gap-1">
                            <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                            <span className="truncate">
                              Unit:{" "}
                              {request.addressLine2Correction || "Remove unit"}
                            </span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm">
                          {getTimeSince(request.createdAt)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(request.createdAt)}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {canMarkActioned(request) && (
                          <Button
                            size="sm"
                            variant="outline"
                            aria-label={`Review change for SHP-${request.shipmentNumber}`}
                            onClick={() => handleAction(request, "action")}
                            className="border-green-200 text-green-600 hover:bg-green-50"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAction(request, "reject")}
                          className="border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <HandleRequestDialog
        key={JSON.stringify([
          selectedRequest?.id,
          selectedRequest?.requestTypes,
          selectedRequest?.contactName,
          selectedRequest?.contactPhone,
          selectedRequest?.contactEmail,
          selectedRequest?.deliveryInstructions,
          dialogAction,
          dialogOpen,
        ])}
        request={selectedRequest}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        action={dialogAction}
        onDecline={() => setDialogAction("reject")}
      />
    </>
  );
}
